import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Paperclip, Download, MapPin, UserCheck, StickyNote } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Field, formatDateTime } from "@/components/admin/ui";
import { STATUS_META, MANUAL_STATUSES, NON_QUALIFIABLE_STATUSES, SOURCE_META, channelForContactMethod, contactMethodIcon, serviceAreaLabel } from "./frontDeskMeta";

const TABS = ["Details", "Activity", "Notes", "Files"];

const NotesPanel = ({ submissionId, API, onError }) => {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/org/front-desk/submissions/${submissionId}/notes`, { withCredentials: true })
      .then(({ data }) => setNotes(data.notes))
      .catch((err) => onError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, submissionId, onError]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/org/front-desk/submissions/${submissionId}/notes`, { text: text.trim() }, { withCredentials: true });
      setText("");
      load();
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="mt-5 space-y-4" data-testid="intake-detail-notes">
      <div className="flex items-start gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add an internal note — staff-only, never visible to the customer…"
          rows={2}
          data-testid="intake-note-input"
          className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white"
        />
        <button
          type="button"
          onClick={addNote}
          disabled={saving || !text.trim()}
          data-testid="intake-note-add-btn"
          className="shrink-0 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Add Note
        </button>
      </div>
      {!notes ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : notes.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 py-10 text-center dark:border-white/10" data-testid="intake-detail-notes-empty">
          <StickyNote size={20} className="mb-2 text-slate-400" />
          <p className="text-sm font-bold text-slate-900 dark:text-white">No internal notes yet.</p>
          <p className="mt-1 max-w-xs text-xs text-slate-500 dark:text-slate-400">Notes here are staff-only and never visible to the customer.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="intake-detail-notes-list">
          {notes.map((n) => (
            <div key={n.event_id} className="rounded-xl border border-slate-200 p-3 text-sm dark:border-white/10" data-testid={`intake-note-${n.event_id}`}>
              <p className="whitespace-pre-wrap text-slate-900 dark:text-white">{n.body}</p>
              <p className="mt-1 text-xs text-slate-400">{n.actor_name || "Staff"} · {formatDateTime(n.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ActionButton = ({ children, onClick, disabled, title, tone = "default", testId }) => {
  const tones = {
    default: "border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10",
    primary: "border-transparent bg-[#0052FF] text-white hover:bg-[#0040CC]",
    danger: "border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={`rounded-xl border px-3.5 py-2 text-xs font-bold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40 ${tones[tone]}`}
    >
      {children}
    </button>
  );
};

export const IntakeDetailDrawer = ({ submission, channels, salesEnabled, onStatusChange, onQualified, onError, API }) => {
  const [tab, setTab] = useState("Details");
  const [busy, setBusy] = useState(false);
  const s = submission;
  const sourceMeta = SOURCE_META[s.source] || { label: s.source, icon: MapPin };
  const SourceIcon = sourceMeta.icon;
  const channelKey = channelForContactMethod(s.preferred_contact_method);
  const channelConnected = channelKey ? channels?.[channelKey] : null;
  const ContactIcon = contactMethodIcon(s.preferred_contact_method);
  const areaInfo = serviceAreaLabel(s.service_area_result);
  const isTerminalOrQualified = NON_QUALIFIABLE_STATUSES.has(s.status);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/org/front-desk/submissions/${s.submission_id}`, { status }, { withCredentials: true });
      onStatusChange(s.submission_id, status);
    } catch (err) {
      onError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const qualifyAsLead = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/org/front-desk/submissions/${s.submission_id}/qualify-lead`, {}, { withCredentials: true });
      onQualified(s.submission_id, data);
    } catch (err) {
      onError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const activity = [
    { ts: s.created_at, label: `Intake received via ${sourceMeta.label}` },
    ...(s.reviewed_at && s.reviewed_at !== s.created_at ? [{ ts: s.reviewed_at, label: `Status set to ${STATUS_META[s.status]?.label || s.status}` }] : []),
    ...(s.qualified_at ? [{ ts: s.qualified_at, label: `Qualified as Lead ${s.qualified_lead_display_id}` }] : []),
  ].sort((a, b) => new Date(b.ts) - new Date(a.ts));

  return (
    <div data-testid="intake-detail-drawer">
      <div className="flex items-center gap-2 border-b border-slate-200 dark:border-white/10">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            data-testid={`intake-detail-tab-${t.toLowerCase()}`}
            className={`border-b-2 px-3 py-2.5 text-sm font-bold transition-colors duration-150 ${tab === t ? "border-[#0052FF] text-[#0052FF]" : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Details" && (
        <div className="mt-5 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Intake ID" value={s.display_id} testId="intake-detail-id" />
            <Field label="Source" value={sourceMeta.label} testId="intake-detail-source" />
            <Field label="Customer" value={`${s.first_name} ${s.last_name}`} testId="intake-detail-name" />
            <Field label="Requested Service" value={s.selected_service_name} testId="intake-detail-service" />
            <Field label="Phone" value={s.phone} testId="intake-detail-phone" />
            <Field label="Email" value={s.email} testId="intake-detail-email" />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Preferred Contact Method</p>
            <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-white" data-testid="intake-detail-preferred-contact">
              {ContactIcon && <ContactIcon size={14} className="text-slate-400" />}
              {s.preferred_contact_method || "—"}
              {channelKey && !channelConnected && (
                <span className="text-xs font-bold text-amber-600 dark:text-amber-400" data-testid="intake-detail-channel-warning">
                  — {channelKey === "sms" ? "SMS" : "Email"} not connected
                </span>
              )}
            </div>
          </div>

          {s.service_address && (
            <Field
              label="Service Address"
              value={`${s.service_address.street}${s.service_address.unit ? `, ${s.service_address.unit}` : ""}, ${s.service_address.city}, ${s.service_address.state} ${s.service_address.zip_code}`}
              testId="intake-detail-address"
            />
          )}

          {s.service_address && (
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Service Area</p>
              <p className={`mt-1 text-sm font-semibold ${areaInfo.cls}`} data-testid="intake-detail-service-area">{areaInfo.label}</p>
              {s.service_area_result?.suggested_teams?.length > 0 && (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-testid="intake-detail-suggested-team">
                  Suggested team: {s.service_area_result.suggested_teams.map((t) => t.name).join(", ")}
                </p>
              )}
            </div>
          )}

          {s.request_description && (
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Customer's Request</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800 dark:text-slate-200" data-testid="intake-detail-description">{s.request_description}</p>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-white/10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Status</p>
              {s.status === "qualified" ? (
                <p className="mt-1 flex items-center gap-1.5 text-sm font-bold text-emerald-600 dark:text-emerald-400" data-testid="intake-detail-qualified-lead">
                  <UserCheck size={14} /> Qualified — Lead{" "}
                  <a href={`/dashboard/solutions/sales?lead=${s.qualified_lead_id}`} data-testid="intake-detail-view-in-sales" className="underline hover:text-emerald-700 dark:hover:text-emerald-300">
                    {s.qualified_lead_display_id} → View in Sales
                  </a>
                </p>
              ) : (
                <select
                  value={s.status}
                  disabled={busy}
                  onChange={(e) => setStatus(e.target.value)}
                  data-testid="intake-detail-status-select"
                  className="mt-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white"
                >
                  {MANUAL_STATUSES.map((k) => <option key={k} value={k}>{STATUS_META[k].label}</option>)}
                </select>
              )}
            </div>
            <p className="text-xs text-slate-400" data-testid="intake-detail-received">Received {formatDateTime(s.created_at)}</p>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4 dark:border-white/10">
            <ActionButton
              tone="primary"
              disabled={busy || !salesEnabled || isTerminalOrQualified}
              title={!salesEnabled ? "The Sales solution isn't enabled for your organization" : undefined}
              onClick={qualifyAsLead}
              testId="intake-action-qualify-lead"
            >
              Qualify as Lead
            </ActionButton>
            <ActionButton disabled title="Routing isn't available yet" testId="intake-action-assign">Assign / Route</ActionButton>
            <ActionButton
              disabled={!channelKey || !channelConnected}
              title={!channelKey ? "No preferred contact method on file" : !channelConnected ? `${channelKey === "sms" ? "SMS" : "Email"} not connected` : undefined}
              testId="intake-action-contact"
            >
              Contact Customer
            </ActionButton>
            <ActionButton disabled={busy || isTerminalOrQualified} onClick={() => setStatus("closed")} testId="intake-action-close">Close Intake</ActionButton>
            <ActionButton disabled={busy || isTerminalOrQualified} onClick={() => setStatus("duplicate")} testId="intake-action-duplicate">Mark Duplicate</ActionButton>
            <ActionButton tone="danger" disabled={busy || isTerminalOrQualified} onClick={() => setStatus("spam")} testId="intake-action-spam">Mark Spam</ActionButton>
            <ActionButton tone="danger" disabled={busy || isTerminalOrQualified} onClick={() => setStatus("declined")} testId="intake-action-decline">Decline</ActionButton>
          </div>
        </div>
      )}

      {tab === "Activity" && (
        <div className="mt-5 space-y-3" data-testid="intake-detail-activity">
          {activity.map((a, i) => (
            <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-200 p-3 text-sm dark:border-white/10">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0052FF]" />
              <div>
                <p className="font-semibold text-slate-900 dark:text-white">{a.label}</p>
                <p className="text-xs text-slate-400">{formatDateTime(a.ts)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Notes" && <NotesPanel submissionId={s.submission_id} API={API} onError={onError} />}

      {tab === "Files" && (
        <div className="mt-5" data-testid="intake-detail-files">
          {(!s.attachments || s.attachments.length === 0) ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No files were attached to this intake.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {s.attachments.map((a) => (
                <a key={a.index} href={`${API}/org/front-desk/submissions/${s.submission_id}/attachments/${a.index}`} target="_blank" rel="noreferrer" data-testid={`intake-detail-attachment-${a.index}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10">
                  <Paperclip size={12} /> File {a.index + 1} <Download size={12} />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
